import { useCallback, useEffect, useRef, useState } from 'react';

import { claimPageUrl } from '../../../../../config/app';
import { IFRAME_TIMEOUT_MS } from '../../../../../constants/upload';
import {
  IExperienceInitPayload,
  IExperiencePreviewPage,
  IExperiencePreviewValues,
  IframeExperienceEventsEnum,
  IframeStatusEnum,
} from '../../../../../types/templates';

interface IIframeMessage<T> {
  type: IframeExperienceEventsEnum;
  payload?: T;
}

interface IUseExperienceBuilderPreviewOptions {
  targetOrigin?: string;
  getNewTabInitPayload?: () => IExperienceInitPayload | null;
  onReset?: () => void;
}

export const useExperienceBuilderPreview = ({
  targetOrigin = claimPageUrl,
  getNewTabInitPayload,
  onReset,
}: IUseExperienceBuilderPreviewOptions = {}) => {
  const [status, setStatus] = useState<IframeStatusEnum>(IframeStatusEnum.Loading);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const newTabWindowRef = useRef<Window | null>(null);
  const isNewTabReady = useRef<boolean>(false);
  const isIframeLoadedRef = useRef<boolean>(false);
  const getNewTabInitPayloadRef = useRef(getNewTabInitPayload);
  getNewTabInitPayloadRef.current = getNewTabInitPayload;
  const onResetRef = useRef(onReset);
  onResetRef.current = onReset;

  const handleIframeError = useCallback(() => {
    setStatus(IframeStatusEnum.Error);
  }, []);

  const postMessage = useCallback(
    <T>(type: IframeExperienceEventsEnum, payload?: T) => {
      const iframe = iframeRef.current;

      if (!targetOrigin) {
        console.warn('postMessage skipped: targetOrigin (REACT_APP_CLAIM_PAGE_URL) is not configured');
        return;
      }

      if (iframe?.contentWindow) {
        const message: IIframeMessage<T> = { type, payload };
        try {
          iframe.contentWindow.postMessage(message, targetOrigin);
        } catch (error) {
          handleIframeError();
          console.error(error);
        }
      }
    },
    [targetOrigin, handleIframeError],
  );

  const handleIframeLoad = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setStatus(IframeStatusEnum.Loading);
    isIframeLoadedRef.current = true;
    onResetRef.current?.();
    postMessage(IframeExperienceEventsEnum.Ping);

    timeoutRef.current = setTimeout(() => {
      setStatus((prev: IframeStatusEnum) => (prev !== IframeStatusEnum.Ready ? IframeStatusEnum.Error : prev));
    }, IFRAME_TIMEOUT_MS);
  }, [postMessage]);

  const reloadCountRef = useRef<number>(0);

  const handleRetry = useCallback(() => {
    if (!iframeRef.current) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    isIframeLoadedRef.current = false;
    onResetRef.current?.();
    setStatus(IframeStatusEnum.Loading);

    reloadCountRef.current += 1;
    iframeRef.current.src = `${targetOrigin}/iframe?retry=${reloadCountRef.current}`;
  }, [targetOrigin]);

  const postMessageToNewTab = useCallback(
    <T>(type: IframeExperienceEventsEnum, payload?: T) => {
      const tab = newTabWindowRef.current;
      if (tab && !tab.closed && targetOrigin) {
        const message: IIframeMessage<T> = { type, payload };
        try {
          tab.postMessage(message, targetOrigin);
        } catch (error) {
          console.error(error);
        }
      }
    },
    [targetOrigin],
  );

  const handleOpenInNewTab = useCallback(() => {
    isNewTabReady.current = false;
    newTabWindowRef.current = window.open(`${targetOrigin}/iframe`, '_blank');
  }, [targetOrigin]);

  const navigate = useCallback(
    (path: IExperiencePreviewPage) => {
      postMessage(IframeExperienceEventsEnum.Navigate, path);
    },
    [postMessage],
  );

  const sendInit = useCallback(
    (payload: IExperienceInitPayload) => {
      postMessage(IframeExperienceEventsEnum.Init, payload);
    },
    [postMessage],
  );

  const setExperience = useCallback(
    (experience: IExperiencePreviewValues) => {
      postMessage(IframeExperienceEventsEnum.SetExperience, experience);
    },
    [postMessage],
  );

  useEffect(() => {
    if (status !== IframeStatusEnum.Loading) {
      return;
    }

    // Arm the error timeout here too (not only in handleIframeLoad): on the
    // from-list path onLoad may never fire, so the timeout must be driven by the
    // Loading state, otherwise a genuinely failed handshake would spin forever
    // with no "Preview unavailable" fallback.
    if (!timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        setStatus((prev: IframeStatusEnum) => (prev !== IframeStatusEnum.Ready ? IframeStatusEnum.Error : prev));
      }, IFRAME_TIMEOUT_MS);
    }

    const interval = setInterval(() => {
      // Decoupled from onLoad: ping as soon as the iframe has a contentWindow.
      // On the deployed HTTPS from-list path the cached iframe can fire (or miss)
      // its load event before React attaches onLoad, leaving isIframeLoadedRef
      // false forever — which would silently stall the handshake. Gating on the
      // live contentWindow instead makes the ping loop self-healing.
      if (!iframeRef.current?.contentWindow) {
        return;
      }
      postMessage(IframeExperienceEventsEnum.Ping);
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [status, postMessage]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== targetOrigin) {
        return;
      }

      if (!event.data || typeof event.data !== 'object') {
        return;
      }

      const { type } = event.data;
      const validEventTypes = Object.values(IframeExperienceEventsEnum) as string[];
      if (!validEventTypes.includes(type)) {
        return;
      }

      if (type === IframeExperienceEventsEnum.Ready) {
        if (event.source === newTabWindowRef.current) {
          isNewTabReady.current = true;
          const payload = getNewTabInitPayloadRef.current?.();
          if (payload) {
            postMessageToNewTab(IframeExperienceEventsEnum.Init, payload);
          }
        } else {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          setStatus(IframeStatusEnum.Ready);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener('message', handleMessage);
    };
  }, [targetOrigin, postMessageToNewTab]);

  return {
    iframeRef,
    status,
    handleIframeLoad,
    handleIframeError,
    handleRetry,
    postMessage,
    navigate,
    sendInit,
    setExperience,
    handleOpenInNewTab,
    postMessageToNewTab,
  };
};
